import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as ProblemIcon,
  RemoveCircleOutline as PendingIcon,
  WarningAmber as WarnIcon
} from '@mui/icons-material';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getBillingFacts, getTrackVerdict } from './billingFacts';

/**
 * Einrichtungs-Assistent für die Kassenabrechnung
 * (nextcab_mobile/docs/general/krankenfahrten_tarife_konzept.md §8.3).
 *
 * Führt den Unternehmer von „ich rechne heute manuell ab" zu „mein Export
 * passt". Die Angaben landen in `appSettings/medicalBilling`.
 *
 * WICHTIG: In dieses Dokument gehören ausschließlich Betriebsstammdaten
 * (Institutionskennzeichen, Abrechnungs-/Tarifkennzeichen, Verkehrsform) —
 * das sind Geschäftsidentifikatoren, keine Geheimnisse. Zugangsdaten für eine
 * spätere API-Anbindung dürfen hier NICHT hinein: `appSettings` ist per
 * Firestore-Rules öffentlich lesbar (`allow read: if true`, damit die App
 * Store-URLs und Programmstatus ohne Login lesen kann).
 */

const PROVIDER_OPTIONS = [
  {
    value: 'dmrz',
    label: 'DMRZ (Deutsches Medizinrechenzentrum)',
    hint: 'Abrechnung nach § 302 SGB V über die DMRZ-Plattform. Benötigt Abrechnungscode und Tarifkennzeichen aus Ihrem DMRZ-Konto.'
  },
  {
    value: 'optadata',
    label: 'opta data / CareMan',
    hint: 'Abrechnung über opta data, Erfassung in CareMan Office. Der Export wird dort importiert.'
  },
  {
    value: 'none',
    label: 'Kein Abrechnungszentrum',
    hint: 'Sie rechnen selbst oder auf Papier ab. Der CSV-Export steht trotzdem zur Verfügung.'
  }
];

const TRANSPORT_MODE_OPTIONS = [
  { value: 'taxi', label: 'Taxi' },
  { value: 'mietwagen', label: 'Mietwagen' }
];

const EMPTY_CONFIG = {
  provider: '',
  ownIk: '',
  operatorName: '',
  billingCode: '',
  tariffCode: '',
  transportMode: 'taxi',
  exportMethod: 'csv'
};

// Werte kommen aus Firestore und koennen null/Zahl sein — nie direkt .trim().
const asText = (value) => (value === null || value === undefined ? '' : String(value));
const isNineDigits = (value) => /^\d{9}$/.test(asText(value).trim());

/**
 * Vollständigkeitsprüfung für den Prüflauf (Schritt 4). Bewusst keine Datei,
 * sondern eine Checkliste: Der Unternehmer soll sehen, was seinem Export noch
 * fehlt, bevor er ihn beim Abrechnungszentrum einreicht.
 */
const buildReadinessChecks = (rides, config) => {
  const relevant = rides.filter(ride => (ride.medicalTransportBillingStatus || 'not_ready') !== 'paid');
  const count = (predicate) => relevant.filter(predicate).length;

  const checks = [
    {
      key: 'ownIk',
      label: 'Eigenes Institutionskennzeichen hinterlegt',
      problems: isNineDigits(config.ownIk) ? 0 : 1,
      detail: 'Ohne IK Ihres Unternehmens kann kein Abrechnungsfall zugeordnet werden.',
      global: true
    },
    {
      key: 'payerIk',
      label: 'Kostenträgerkennung (IK) je Fahrt',
      problems: count(ride => !(ride.medicalTransportSnapshot || {}).healthInsuranceIk),
      detail: 'Steht auf Muster 4 in der Kopfzeile. Fehlt sie, tragen Sie sie in der Verordnung nach.'
    },
    {
      key: 'insuranceNumber',
      label: 'Versichertennummer je Fahrt',
      problems: count(ride => !(ride.medicalTransportSnapshot || {}).insuranceNumber),
      detail: 'Pflichtangabe für die Abrechnung nach § 302 SGB V.'
    },
    {
      key: 'occupiedKm',
      label: 'Besetzt-Kilometer ermittelt',
      problems: count(ride => {
        const facts = getBillingFacts(ride);
        return !facts || facts.occupiedKm === null || facts.occupiedKm === undefined;
      }),
      detail: 'Werden automatisch aus dem Fahrertracking ermittelt. Fehlen sie, hilft „Neu berechnen" im Tab „Fahrten".'
    },
    {
      key: 'trackQuality',
      label: 'Streckendaten belastbar',
      problems: count(ride => {
        const verdict = getTrackVerdict(ride);
        return verdict === 'review' || verdict === 'unusable';
      }),
      detail: 'Fahrten mit lückenhafter Aufzeichnung sollten Sie vor dem Export ansehen.'
    },
    {
      key: 'tripDirection',
      label: 'Fahrtrichtung eindeutig',
      problems: count(ride => getBillingFacts(ride)?.tripDirection === 'unknown'),
      detail: 'Hin- und Rückfahrt sind getrennte Positionen im Kassenvertrag.'
    },
    {
      key: 'proof',
      label: 'Beförderungsnachweis vorhanden',
      problems: count(ride => {
        const status = ride.medicalTransportProofStatus || 'missing';
        return status !== 'complete' && status !== 'waived';
      }),
      detail: 'Unterschrift des Patienten bzw. dokumentierter Ersatznachweis.'
    }
  ];

  if (config.provider === 'dmrz') {
    checks.splice(1, 0, {
      key: 'tariffCode',
      label: 'Abrechnungscode und Tarifkennzeichen hinterlegt',
      problems: asText(config.billingCode).trim() && asText(config.tariffCode).trim() ? 0 : 1,
      detail: 'DMRZ zieht darüber automatisch den passenden Kassenvertrag.',
      global: true
    });
  }

  return { checks, relevantCount: relevant.length };
};

const MedicalBillingSetupTab = ({ rides, ridesLoading }) => {
  const currentUser = useSelector(state => state.auth.user);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'medicalBilling'),
      (snapshot) => {
        const data = snapshot.data() || {};
        setConfig({ ...EMPTY_CONFIG, ...data });
        setSavedAt(data.updatedAt || null);
      },
      (snapshotError) => {
        console.error('Abrechnungs-Einstellungen konnten nicht geladen werden:', snapshotError);
        setError('Einstellungen konnten nicht geladen werden.');
      }
    );
    return () => unsubscribe();
  }, []);

  const readiness = useMemo(() => buildReadinessChecks(rides || [], config), [rides, config]);

  const updateField = (field) => (event) => {
    setConfig(previous => ({ ...previous, [field]: event.target.value }));
  };

  const persist = async (patch) => {
    setSaving(true);
    setError('');
    try {
      await setDoc(
        doc(db, 'appSettings', 'medicalBilling'),
        {
          ...config,
          ...patch,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null
        },
        { merge: true }
      );
    } catch (saveError) {
      console.error('Abrechnungs-Einstellungen konnten nicht gespeichert werden:', saveError);
      setError(saveError.message || 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const saveAndNext = async (patch = {}) => {
    await persist(patch);
    setActiveStep(step => step + 1);
  };

  const ikError = asText(config.ownIk).trim().length > 0 && !isNineDigits(config.ownIk);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Abrechnungszentrum einrichten</Typography>
          {savedAt && <Chip size="small" color="success" variant="outlined" label="Gespeichert" />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Diese Angaben brauchen wir, damit Ihr Export beim Abrechnungszentrum
          angenommen wird. Sie können den Assistenten jederzeit erneut
          durchlaufen — bestehende Werte bleiben erhalten.
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          <Step completed={!!config.provider}>
            <StepLabel onClick={() => setActiveStep(0)} sx={{ cursor: 'pointer' }}>
              Abrechnungsweg wählen
            </StepLabel>
            <StepContent>
              <RadioGroup value={asText(config.provider)} onChange={updateField('provider')}>
                {PROVIDER_OPTIONS.map(option => (
                  <Box key={option.value} sx={{ mb: 1 }}>
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                      {option.hint}
                    </Typography>
                  </Box>
                ))}
              </RadioGroup>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                disabled={!config.provider || saving}
                onClick={() => saveAndNext()}
              >
                Weiter
              </Button>
            </StepContent>
          </Step>

          <Step completed={isNineDigits(config.ownIk)}>
            <StepLabel onClick={() => setActiveStep(1)} sx={{ cursor: 'pointer' }}>
              Stammdaten Ihres Unternehmens
            </StepLabel>
            <StepContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Institutionskennzeichen (IK)"
                    value={asText(config.ownIk)}
                    onChange={updateField('ownIk')}
                    error={ikError}
                    helperText={ikError ? 'Ein IK hat genau 9 Ziffern.' : 'Ihr eigenes IK, vergeben von der ARGE·IK'}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Name des Leistungserbringers"
                    value={asText(config.operatorName)}
                    onChange={updateField('operatorName')}
                    helperText="Wie im Vertrag mit den Kassen hinterlegt"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="transport-mode-label">Verkehrsform</InputLabel>
                    <Select
                      labelId="transport-mode-label"
                      label="Verkehrsform"
                      value={asText(config.transportMode) || 'taxi'}
                      onChange={updateField('transportMode')}
                    >
                      {TRANSPORT_MODE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {config.provider === 'dmrz' && (
                  <>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        label="Abrechnungscode"
                        value={asText(config.billingCode)}
                        onChange={updateField('billingCode')}
                        helperText="z. B. 46"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        label="Tarifkennzeichen"
                        value={asText(config.tariffCode)}
                        onChange={updateField('tariffCode')}
                        helperText="z. B. 21H65"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="info">
                        Über Abrechnungscode und Tarifkennzeichen zieht DMRZ
                        automatisch den passenden Kassenvertrag und berechnet
                        Netto, Brutto, Umsatzsteuer und Zuzahlung selbst.
                        <strong> Preise liefern wir nicht</strong> — wir liefern
                        Fahrt-, Patienten- und Verordnungsdaten samt
                        Besetzt-Kilometern.
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button onClick={() => setActiveStep(0)}>Zurück</Button>
                <Button
                  variant="contained"
                  disabled={saving || ikError}
                  onClick={() => saveAndNext()}
                >
                  Weiter
                </Button>
              </Stack>
            </StepContent>
          </Step>

          <Step completed={!!config.exportMethod}>
            <StepLabel onClick={() => setActiveStep(2)} sx={{ cursor: 'pointer' }}>
              Übergabeweg
            </StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Derzeit: Datei-Übergabe</AlertTitle>
                Sie exportieren die abrechnungsbereiten Fahrten im Tab „Fahrten"
                als CSV und laden sie in Ihrem Abrechnungszentrum hoch.
              </Alert>
              <Alert severity="warning">
                Eine direkte Schnittstelle (Übergabe per API und Rückmeldung der
                bewerteten Beträge) ist in Vorbereitung und hängt an einer
                offenen Rückfrage bei DMRZ bzw. opta data. Zugangsdaten werden
                deshalb hier bewusst noch nicht abgefragt — und sie werden
                später auch nicht in diesen Einstellungen gespeichert, da diese
                für die mobile App öffentlich lesbar sind.
              </Alert>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button onClick={() => setActiveStep(1)}>Zurück</Button>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={() => saveAndNext({ exportMethod: 'csv' })}
                >
                  Weiter
                </Button>
              </Stack>
            </StepContent>
          </Step>

          <Step>
            <StepLabel onClick={() => setActiveStep(3)} sx={{ cursor: 'pointer' }}>
              Prüflauf
            </StepLabel>
            <StepContent>
              {/* Ohne geladene Fahrten wären alle Prüfungen trivial grün —
                  das wäre eine falsche Entwarnung. */}
              {ridesLoading ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Fahrten werden geladen — der Prüflauf startet gleich.
                </Alert>
              ) : readiness.relevantCount === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Im geladenen Zeitraum gibt es keine offenen Krankenfahrten.
                  Die fahrtbezogenen Prüfungen sind deshalb ohne Aussage — die
                  Stammdaten-Prüfungen unten gelten trotzdem.
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Geprüft werden {readiness.relevantCount} noch nicht bezahlte
                  Krankenfahrten im aktuell geladenen Zeitraum.
                </Typography>
              )}
              <Stack spacing={1}>
                {readiness.checks.map(check => {
                  // Fahrtbezogene Prüfungen ohne Datengrundlage nicht als
                  // bestanden ausweisen.
                  const inconclusive =
                    !check.global && (ridesLoading || readiness.relevantCount === 0);
                  const ok = !inconclusive && check.problems === 0;
                  return (
                    <Paper key={check.key} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {inconclusive
                          ? <PendingIcon color="disabled" fontSize="small" />
                          : ok
                            ? <OkIcon color="success" fontSize="small" />
                            : (check.global ? <ProblemIcon color="error" fontSize="small" /> : <WarnIcon color="warning" fontSize="small" />)}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {check.label}
                          </Typography>
                          {inconclusive ? (
                            <Typography variant="caption" color="text.secondary">
                              Keine Fahrten zum Prüfen geladen.
                            </Typography>
                          ) : !ok && (
                            <Typography variant="caption" color="text.secondary">
                              {check.global
                                ? check.detail
                                : `${check.problems} Fahrt${check.problems === 1 ? '' : 'en'} betroffen — ${check.detail}`}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Offene Punkte blockieren den Export nicht — sie sind die Liste
                dessen, was das Abrechnungszentrum vermutlich beanstanden würde.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button onClick={() => setActiveStep(2)}>Zurück</Button>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={() => persist({ setupCompletedAt: serverTimestamp() })}
                >
                  Einrichtung abschließen
                </Button>
              </Stack>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>
    </Box>
  );
};

export default MedicalBillingSetupTab;
